import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';

import { useTranslate } from 'hooks/useTranslate';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { store } from 'redux/store';

import { cancelIncentiveOffer } from 'modules/incentivesMarket/redux/incentiveOfferActions';
import { getIncentiveOfferManageActionsVisibility } from 'modules/incentivesMarket/utils/canManageIncentiveOffer';

import { IncentiveOffer, IncentiveOfferType } from 'modules/incentivesMarket/types';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

type IncentiveOfferManageActionsProps = {
  offer: IncentiveOffer;
  societyId: string;
  isAdmin: boolean;
  userBeneficiaryIds: string[];
  beneficiaryQuerySuffix: string;
};

const IncentiveOfferManageActions: FC<IncentiveOfferManageActionsProps> = ({
  offer,
  societyId,
  isAdmin,
  userBeneficiaryIds,
  beneficiaryQuerySuffix,
}) => {
  const { t } = useTranslate();
  const history = useHistory();

  const { canModify, canCancel } = getIncentiveOfferManageActionsVisibility({
    offer,
    isAdmin,
    userBeneficiaryIds,
  });

  if (!canModify && !canCancel) {
    return null;
  }

  const publisherBeneficiaryId =
    typeof offer.beneficiary === 'string'
      ? offer.beneficiary
      : offer.beneficiary?._id || '';

  const partnerEditQuery = publisherBeneficiaryId
    ? `?beneficiaryId=${publisherBeneficiaryId}`
    : '';

  const handleModify = () => {
    if (offer.type === IncentiveOfferType.SELL) {
      history.push(
        `/editar-oferta-venta-incentivos/${societyId}/${offer._id}${partnerEditQuery}`
      );
      return;
    }
    history.push(
      `/editar-oferta-compra-incentivos/${societyId}/${offer._id}${partnerEditQuery}`
    );
  };

  const handleCancelOffer = () => {
    Swal.fire({
      icon: 'warning',
      title: t('CancelOfferConfirmationTitle'),
      text: t('CancelOfferConfirmation'),
      showCancelButton: true,
      confirmButtonText: t('Confirm'),
      cancelButtonText: t('Cancel'),
      confirmButtonColor: '#6576ff',
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }
      try {
        await store.dispatch(cancelIncentiveOffer(offer._id));
        successSwal(t('OfferCanceledSuccessfully'));
        history.push(`/ofertas-incentivos/${societyId}${beneficiaryQuerySuffix}`);
      } catch {
        errorSwal(t('ErrorCancelingOffer'));
      }
    });
  };

  return (
    <>
      {canModify && (
        <li>
          <Button
            variant={ButtonVariants.GRAY}
            data-testid="trading-offer-modify-button"
            onClick={handleModify}
          >
            {t('ModifyOffer')}
          </Button>
        </li>
      )}
      {canCancel && (
        <li>
          <Button
            variant={ButtonVariants.DANGER}
            data-testid="trading-offer-cancel-button"
            onClick={handleCancelOffer}
          >
            <em className="icon ni ni-cross" />
            <span className="ml-1">{t('CancelOffer')}</span>
          </Button>
        </li>
      )}
    </>
  );
};

export default IncentiveOfferManageActions;
